import { usaConfig } from './usa'
import { uaeConfig } from './uae'
import type { CountryCode, CountryConfig } from './types'

// ===========================================
// REGISTRY
// ===========================================

const countryConfigs: Record<CountryCode, CountryConfig> = {
  USA: usaConfig,
  UAE: uaeConfig,
  // Future: UK, Canada, Australia, Singapore
}

// ===========================================
// PUBLIC API
// ===========================================

export function getCountryConfig(code: CountryCode): CountryConfig {
  const config = countryConfigs[code]
  if (!config) {
    throw new Error(
      `Unsupported country code: "${code}". ` +
      `Supported countries: ${Object.keys(countryConfigs).join(', ')}`
    )
  }
  return config
}

export function getSupportedCountries(): CountryCode[] {
  return Object.keys(countryConfigs) as CountryCode[]
}

// Coming soon countries (not yet fully implemented)
const comingSoonCountries: Array<{
  code: string
  name: string
  flag: string
  currencyCode: string
}> = [
  { code: 'UAE', name: 'United Arab Emirates', flag: '🇦🇪', currencyCode: 'AED' },
  { code: 'CAN', name: 'Canada', flag: '🇨🇦', currencyCode: 'CAD' },
]

export function getCountryList(): Array<{
  code: CountryCode | string
  name: string
  flag: string
  currencyCode: string
  comingSoon?: boolean
}> {
  // Only return fully supported countries (USA for now)
  const supported = getSupportedCountries()
    .filter(code => code === 'USA') // Only US is fully supported for launch
    .map(code => ({
      code,
      name: countryConfigs[code].name,
      flag: countryConfigs[code].flag,
      currencyCode: countryConfigs[code].currency.code,
      comingSoon: false,
    }))

  // Add coming soon countries
  const comingSoon = comingSoonCountries.map(c => ({
    ...c,
    comingSoon: true,
  }))

  return [...supported, ...comingSoon]
}

export function isValidCountryCode(code: string): code is CountryCode {
  return code in countryConfigs
}

export function getDefaultCountryCode(): CountryCode {
  return 'USA'
}

// ===========================================
// CONFIG VALIDATION (Runtime)
// ===========================================

export function validateCountryConfig(config: CountryConfig): string[] {
  const errors: string[] = []

  if (!config.code) errors.push('Missing country code')
  if (!config.name) errors.push('Missing country name')
  if (!config.currency?.code) errors.push('Missing currency code')
  if (!config.taxRules) errors.push('Missing tax rules')
  if (!config.assets) errors.push('Missing asset configuration')
  if (!config.taxStrategy) errors.push('Missing tax strategy')
  if (!config.assetStrategy) errors.push('Missing asset strategy')

  // Validate all asset field mappings are unique
  const fieldMappings = new Set<string>()
  for (const category of Object.values(config.assets)) {
    for (const asset of category) {
      if (fieldMappings.has(asset.fieldMapping)) {
        errors.push(`Duplicate field mapping: ${asset.fieldMapping}`)
      }
      fieldMappings.add(asset.fieldMapping)
    }
  }

  return errors
}
